import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AgreementForecast } from '@catalyst-psa/shared';

import { Badge } from '../../components/ui/Badge.js';
import { ForecastGrid, groupContributions, type ForecastGridRow } from './ForecastGrid.js';
import { useAgreementForecast } from './api.js';

/**
 * Filters an agreement forecast by the active/draft toggles and returns the rolled-up
 * month totals + grand total of the visible rows. Shared by the panel and the page-level
 * combined-totals grids so both react to the same toggles.
 */
export function filterAgreementTotals(
  forecast: AgreementForecast | undefined,
  showActive: boolean,
  showDraft: boolean,
): { totalsByMonth: Record<string, string>; total: string } {
  const visible = (forecast?.groups ?? []).filter(
    (g) => (g.status === 'active' && showActive) || (g.status === 'draft' && showDraft),
  );
  const monthTotals: Record<string, number> = {};
  let total = 0;
  for (const g of visible) {
    for (const [month, amount] of Object.entries(g.totalsByMonth)) {
      monthTotals[month] = (monthTotals[month] ?? 0) + Number(amount);
      total += Number(amount);
    }
  }
  const monthTotalsStr: Record<string, string> = {};
  for (const [month, amount] of Object.entries(monthTotals))
    monthTotalsStr[month] = amount.toFixed(2);
  return { totalsByMonth: monthTotalsStr, total: total.toFixed(2) };
}

interface AgreementForecastPanelProps {
  /** When false, the panel does not fetch (e.g. user lacks the agreements permission). */
  enabled?: boolean;
  defaultCollapsed?: boolean;
  /** Pre-fetched forecast (e.g. the public portal). When provided, the panel does not fetch. */
  data?: AgreementForecast;
  /** When false, drill-down rows do not navigate (read-only public view). */
  interactive?: boolean;
  /** Controlled "Active" toggle (lifts state to the page so combined totals react). */
  showActive?: boolean;
  onShowActiveChange?: (value: boolean) => void;
  /** Controlled "Draft" toggle. */
  showDraft?: boolean;
  onShowDraftChange?: (value: boolean) => void;
}

/**
 * Forward-looking recurring-revenue forecast for agreements, grouped by type & subtype.
 * Active and draft agreements are separate rows; the two statuses can be toggled on/off
 * and the month totals recompute from the visible rows. Drill-down opens the agreement.
 * The toggles can be controlled by a parent so page-level combined totals stay in sync.
 */
export function AgreementForecastPanel({
  enabled = true,
  defaultCollapsed,
  data,
  interactive = true,
  showActive: showActiveProp,
  onShowActiveChange,
  showDraft: showDraftProp,
  onShowDraftChange,
}: AgreementForecastPanelProps): JSX.Element {
  const navigate = useNavigate();
  const query = useAgreementForecast(enabled && data === undefined);
  const [internalActive, setInternalActive] = useState(true);
  const [internalDraft, setInternalDraft] = useState(true);
  const showActive = showActiveProp ?? internalActive;
  const showDraft = showDraftProp ?? internalDraft;
  const setShowActive = onShowActiveChange ?? setInternalActive;
  const setShowDraft = onShowDraftChange ?? setInternalDraft;

  const forecast = data ?? query.data;
  const months = forecast?.months ?? [];

  const { rows, totalsByMonth, grandTotal } = useMemo(() => {
    const visible = (forecast?.groups ?? []).filter(
      (g) => (g.status === 'active' && showActive) || (g.status === 'draft' && showDraft),
    );
    const gridRows: ForecastGridRow[] = visible.map((g) => ({
      key: g.key,
      label: (
        <span className="inline-flex items-center gap-2">
          <Badge tone={g.status === 'active' ? 'green' : 'amber'}>
            {g.status === 'active' ? 'Active' : 'Draft'}
          </Badge>
          <span>{g.typeLabel}</span>
          <span className="text-slate-400">· {g.subtype ?? 'No subtype'}</span>
        </span>
      ),
      total: g.total,
      totalsByMonth: g.totalsByMonth,
      contributions: groupContributions(g.contributions),
    }));

    const { totalsByMonth: monthTotalsStr, total } = filterAgreementTotals(
      forecast,
      showActive,
      showDraft,
    );
    return { rows: gridRows, totalsByMonth: monthTotalsStr, grandTotal: total };
  }, [forecast, showActive, showDraft]);

  const toggle = (
    <span className="flex items-center gap-3 text-xs">
      <label className="inline-flex cursor-pointer items-center gap-1.5">
        <input
          type="checkbox"
          checked={showActive}
          onChange={(e) => setShowActive(e.target.checked)}
          className="h-3.5 w-3.5 rounded border-slate-300"
        />
        <span className="font-medium text-emerald-700">Active</span>
      </label>
      <label className="inline-flex cursor-pointer items-center gap-1.5">
        <input
          type="checkbox"
          checked={showDraft}
          onChange={(e) => setShowDraft(e.target.checked)}
          className="h-3.5 w-3.5 rounded border-slate-300"
        />
        <span className="font-medium text-amber-700">Draft</span>
      </label>
    </span>
  );

  return (
    <ForecastGrid
      title="Agreement revenue forecast"
      subtitle="recurring, by type &amp; subtype"
      months={months}
      rows={rows}
      grandTotal={grandTotal}
      totalsByMonth={totalsByMonth}
      onOpenSource={interactive ? (id) => navigate(`/agreements/${id}`) : undefined}
      isLoading={data === undefined && query.isLoading}
      isError={data === undefined && query.isError}
      emptyText="No active or draft agreements with recurring lines to forecast yet."
      headerExtra={toggle}
      defaultCollapsed={defaultCollapsed}
    />
  );
}
